import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { aiService } from '../services/aiService';
import { backendService } from '../services/backendService';
import { useAgentOrchestration } from '../context/AgentOrchestrationContext';
import { KernelSettings, ThreatPriority, VisionMode } from '../types';

const DEFAULT_LAYOUT = [
  { i: 'camera', x: 0, y: 0, w: 6, h: 7 },
  { i: 'controls', x: 0, y: 7, w: 6, h: 5 },
  { i: 'logs', x: 6, y: 0, w: 6, h: 12 }
];

// Threat keywords for priority classification
const HIGH_PRIORITY_KEYWORDS = ['intruz', 'pożar', 'kradzież', 'niebezpieczeństwo', 'emergency', 'intruder', 'fire', 'danger', 'alarm', 'włamanie'];
const NORMAL_PRIORITY_KEYWORDS = ['obcy', 'nieznana osoba', 'ruch', 'zmiana', 'unknown person', 'movement', 'change'];

async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
}

// Fetch with timeout wrapper
async function fetchWithTimeout<T>(promise: Promise<T>, timeoutMs: number = 60000): Promise<T> {
  let timeoutId: NodeJS.Timeout;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(`Request timeout after ${timeoutMs}ms`)), timeoutMs);
  });

  try {
    const result = await Promise.race([promise, timeoutPromise]);
    clearTimeout(timeoutId!);
    return result;
  } catch (e) {
    clearTimeout(timeoutId!);
    throw e;
  }
}

interface VideoAnalysisProps {
  settings: KernelSettings;
  setKernel: React.Dispatch<React.SetStateAction<KernelSettings>>;
  isMainChatActive?: boolean; // Phase 1.4: Coordination with AgentChat
  onThreatDetected?: (priority: ThreatPriority, message: string) => void; // Phase 4: Report to master
}

type ResultType = 'ANALYSIS' | 'VOICE' | 'SYSTEM' | 'FALLBACK' | 'NOMINAL' | 'HIGH_THREAT';

const VideoAnalysis: React.FC<VideoAnalysisProps> = ({
  settings,
  setKernel,
  isMainChatActive = false,
  onThreatDetected
}) => {
  // Phase 4: Connect to orchestration context
  const { sendMessage, subscribe, visionMode: globalVisionMode, setVisionMode: setGlobalVisionMode, mode } = useAgentOrchestration();

  const [isActive, setIsActive] = useState(false);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [guardianPrompt, setGuardianPrompt] = useState('Monitoruj przestrzeń. Opisz co widzisz ciepłym, ludzkim głosem. Jeśli wszystko jest w porządku, odpowiedz: "STATUS NORMALNY: [krótki opis]". Jeśli zauważysz kogoś obcego, zwróć mu grzecznie ale stanowczo uwagę, że teren jest monitorowany.');
  const [results, setResults] = useState<{ timestamp: string, text: string, type: ResultType }[]>([]);
  const [loading, setLoading] = useState(false);
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false);
  // Sync voice state with global mode
  useEffect(() => {
    setIsVoiceEnabled(mode === 'voice' || mode === 'multimodal');
  }, [mode]);

  const [error, setError] = useState<string | null>(null);
  const [sensitivity, setSensitivity] = useState(15);
  const [isListening, setIsListening] = useState(false);

  // Phase 1.1: Exponential backoff state
  const [errorCount, setErrorCount] = useState(0);
  const [retryCountdown, setRetryCountdown] = useState<number | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  // Phase 4: Vision mode control
  const [visionMode, setVisionMode] = useState<VisionMode>('auto');

  // Vision Models
  const [availableModels, setAvailableModels] = useState<any[]>([]);
  const [selectedVisionModel, setSelectedVisionModel] = useState<string>(() => localStorage.getItem('omnigate_observer_vision_model') || '');

  useEffect(() => {
    backendService.fetchModels().then(models => {
      setAvailableModels(models);
      const visionModels = models.filter((m: any) => m.tags?.includes('vision'));
      if (visionModels.length > 0 && !localStorage.getItem('omnigate_observer_vision_model')) {
        setSelectedVisionModel(visionModels[0].id);
      }
    }).catch(console.error);
  }, []);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const prevFrameRef = useRef<ImageData | null>(null);
  const audioContextOut = useRef<AudioContext | null>(null);
  const captureInterval = useRef<number | null>(null);
  const isProcessing = useRef(false); // Phase 1.1: Processing guard
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const silenceTimer = useRef<NodeJS.Timeout | null>(null);
  const analyser = useRef<AnalyserNode | null>(null);
  const audioStream = useRef<MediaStream | null>(null);
  const retryTimer = useRef<NodeJS.Timeout | null>(null);
  const countdownTimer = useRef<NodeJS.Timeout | null>(null);
  // BUG-04 FIX: Ref zamiast sprawdzania errorCount w closure (unikamy stale closure bug)
  const backoffUntilRef = useRef<number>(0);

  // Draggable Layout State
  const [isLocked, setIsLocked] = useState(true);
    const [layouts, setLayouts] = useState<any>({ lg: DEFAULT_LAYOUT });
  const [mounted, setMounted] = useState(false);
    
  // Custom width provider
  const [width, setWidth] = useState(1200);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    const saved = localStorage.getItem('video_analysis_layout');
    if (saved) {
      try { const p = JSON.parse(saved);
                if (p) { Object.keys(p).forEach(k => { if (Array.isArray(p[k])) p[k].forEach((i:any) => delete i.static); }); }
                setLayouts(p); } catch { }
    }

    if (containerRef.current) {
      setWidth(containerRef.current.clientWidth);
    }

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        if (entry.contentRect.width > 0) {
          setWidth(entry.contentRect.width);
        }
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const onLayoutChange = (layout: any, allLayouts: any) => {
    setLayouts(allLayouts);
    localStorage.setItem('video_analysis_layout', JSON.stringify(allLayouts));
  };

  useEffect(() => {
    // Phase 4: Subscribe to messages from master agent
    const unsubscribe = subscribe('vision', (message) => {
      if (message.from === 'master' && message.type === 'command') {
        switch (message.payload?.action) {
          case 'set_vision_mode':
            setVisionMode(message.payload.mode);
            setGlobalVisionMode(message.payload.mode);
            break;
          case 'request_snapshot':
            captureSnapshot().then(result => {
              sendMessage({
                from: 'vision',
                to: 'master',
                type: 'report',
                payload: {
                  type: 'snapshot_result',
                  data: result
                }
              });
            });
            break;
        }
      }
    });

    return () => {
      unsubscribe();
      audioContextOut.current?.close();
      if (retryTimer.current) clearTimeout(retryTimer.current);
      if (countdownTimer.current) clearInterval(countdownTimer.current);
      // BUG-07 FIX: Zatrzymaj stream kamery gdy komponent się odmontowuje (zmiana zakładki)
      // Bez tego kamera pozostaje aktywna w tle po przejściu do innej zakładki
      if (videoRef.current?.srcObject) {
        (videoRef.current.srcObject as MediaStream).getTracks().forEach(t => t.stop());
        videoRef.current.srcObject = null;
      }
      // FIX #3: Clear cross-applet camera bridge on unmount
      (window as any).nexusVisionStream = null;
      (window as any).nexusSnapshot = null;
      // Zatrzymaj loop capture i reset flagi backoff
      if (captureInterval.current) window.clearInterval(captureInterval.current);
      captureInterval.current = null;
      backoffUntilRef.current = 0;
      isProcessing.current = false;
    };
  }, [subscribe, sendMessage, setGlobalVisionMode]);

  const ensureAudioContext = async () => {
    if (!audioContextOut.current) {
      // Don't force sample rate - let browser use native device sample rate
      audioContextOut.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (audioContextOut.current.state === 'suspended') {
      try {
        await audioContextOut.current.resume();
      } catch (error) {
        console.warn('Failed to resume video analysis audio context:', error);
        // Create new context if resume fails
        audioContextOut.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
    }
    return audioContextOut.current;
  };

  // Phase 1.1: Calculate backoff delay
  const calculateBackoffDelay = useCallback((errors: number): number => {
    // Exponential backoff: min(1s * 2^errors, 60s)
    return Math.min(1000 * Math.pow(2, errors), 60000);
  }, []);

  // Phase 1.1: Start retry countdown display
  const startRetryCountdown = useCallback((delayMs: number) => {
    const seconds = Math.ceil(delayMs / 1000);
    setRetryCountdown(seconds);

    if (countdownTimer.current) clearInterval(countdownTimer.current);

    let remaining = seconds;
    countdownTimer.current = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        setRetryCountdown(null);
        if (countdownTimer.current) clearInterval(countdownTimer.current);
      } else {
        setRetryCountdown(remaining);
      }
    }, 1000);
  }, []);

  // Phase 4: Classify threat priority
  const classifyThreatPriority = useCallback((text: string): ThreatPriority => {
    const lowerText = text.toLowerCase();
    for (const keyword of HIGH_PRIORITY_KEYWORDS) {
      if (lowerText.includes(keyword)) return 'HIGH';
    }
    return 'NORMAL';
  }, []);

  useEffect(() => {
    if (isActive && visionMode !== 'paused') startCaptureLoop();
    else stopCaptureLoop();
    return () => stopCaptureLoop();
  }, [isActive, settings.visionFps, guardianPrompt, sensitivity, visionMode]);

  const detectMotion = (ctx: CanvasRenderingContext2D): boolean => {
    const currentFrame = ctx.getImageData(0, 0, 640, 480);
    if (!prevFrameRef.current) {
      prevFrameRef.current = currentFrame;
      return true;
    }

    let diff = 0;
    for (let i = 0; i < currentFrame.data.length; i += 4) {
      const avgCurrent = (currentFrame.data[i] + currentFrame.data[i + 1] + currentFrame.data[i + 2]) / 3;
      const avgPrev = (prevFrameRef.current.data[i] + prevFrameRef.current.data[i + 1] + prevFrameRef.current.data[i + 2]) / 3;
      if (Math.abs(avgCurrent - avgPrev) > 30) diff++;
    }

    prevFrameRef.current = currentFrame;
    const percent = (diff / (640 * 480)) * 100;
    return percent > (20 - sensitivity);
  };

  const startCaptureLoop = () => {
    if (captureInterval.current) window.clearInterval(captureInterval.current);
    const intervalMs = 1000 / settings.visionFps;

    captureInterval.current = window.setInterval(async () => {
      // Phase 1.1: Guard against overlapping requests
      if (isProcessing.current || !videoRef.current || !canvasRef.current || !isActive) return;

      // Phase 4: Skip if paused or on-demand mode
      if (visionMode === 'paused' || visionMode === 'on-demand') return;

      const ctx = canvasRef.current.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(videoRef.current, 0, 0, 640, 480);
      if (!detectMotion(ctx)) return;

      isProcessing.current = true;
      setIsCapturing(true);

      try {
        const base64 = canvasRef.current.toDataURL('image/jpeg', 0.5).split(',')[1];

        // Phase 1.1: Fetch with timeout
        // BUG-04 FIX: Zwiększ timeout z 30s do 90s — qwen3-vl:4b może potrzebować
        // do 60-90s przy pierwszym załadowaniu modelu do VRAM lub przy wolnym GPU
        const responseText = await fetchWithTimeout(
          aiService.analyzeVision(base64, 'image/jpeg', guardianPrompt, true, selectedVisionModel),
          90000
        );

        const timestamp = new Date().toLocaleTimeString();

        if (!responseText || responseText.includes('Error')) {
          throw new Error(responseText || "Empty response");
        }

        // Phase 1.1: Reset error count on success
        setErrorCount(0);
        setLastError(null);
        setRetryCountdown(null);
        if (retryTimer.current) clearTimeout(retryTimer.current);

        const isNominal = responseText.toUpperCase().includes('STATUS NORMALNY') || responseText.toUpperCase().includes('NORMALNY');
        const isFallback = responseText.includes('(QUOTA FALLBACK)') || responseText.includes('(FALLBACK MODE)');

        // Phase 4: Classify threat priority
        const threatPriority = classifyThreatPriority(responseText);

        // Phase 1.4 & 4: Handle voice chat active state
        const shouldSpeak = !isMainChatActive || (threatPriority === 'HIGH' && !isNominal);
        const shouldLog = !isMainChatActive || threatPriority === 'HIGH';

        if (shouldLog) {
          let resultType: ResultType = 'ANALYSIS';
          if (isNominal) resultType = 'NOMINAL';
          else if (isFallback) resultType = 'FALLBACK';
          else if (threatPriority === 'HIGH') resultType = 'HIGH_THREAT';

          setResults(prev => [{
            timestamp,
            text: responseText,
            type: resultType
          }, ...prev].slice(0, 50));
        }

        // Phase 4: Report to master agent via orchestration
        if (threatPriority === 'HIGH' && !isNominal && onThreatDetected) {
          onThreatDetected(threatPriority, responseText);
          // Also send via orchestration context
          sendMessage({
            from: 'vision',
            to: 'master',
            type: 'report',
            payload: {
              type: 'threat_detected',
              priority: threatPriority,
              message: responseText,
              timestamp: Date.now()
            }
          });
        }

        // Phase 1.4: Only trigger TTS if chat not active OR high priority threat
        if (!isNominal && shouldSpeak) {
          await triggerVoiceResponse(responseText);
        }

      } catch (err: any) {
        console.error("Vision Error:", err);
        const timestamp = new Date().toLocaleTimeString();

        // Phase 1.1: Increment error count and schedule retry with backoff
        const newErrorCount = errorCount + 1;
        setErrorCount(newErrorCount);
        setLastError(err.message || 'Vision analysis failed');

        const backoffDelay = calculateBackoffDelay(newErrorCount);
        startRetryCountdown(backoffDelay);

        setResults(prev => [{
          timestamp,
          text: `ERROR: ${err.message || 'Vision analysis failed'}. Ponowna próba za ${Math.ceil(backoffDelay / 1000)}s... (próba ${newErrorCount})`,
          type: 'SYSTEM' as const
        }, ...prev].slice(0, 50));

        // BUG-04 FIX: Użyj ref do śledzenia backoff — unikamy stale closure
        backoffUntilRef.current = Date.now() + backoffDelay;
        if (retryTimer.current) clearTimeout(retryTimer.current);
        retryTimer.current = setTimeout(() => {
          backoffUntilRef.current = 0; // Reset backoff sentinel
          isProcessing.current = false; // Allow next attempt after backoff
        }, backoffDelay);
        // isProcessing.current pozostaje TRUE podczas backoff — NOT reset in finally

      } finally {
        // BUG-04 FIX: Reset isProcessing tylko jeśli NIE jesteśmy w backoff
        // Sprawdzamy ref (nie stale closure errorCount)
        if (Date.now() >= backoffUntilRef.current) {
          isProcessing.current = false;
        }
        // Jeśli backoff aktywny — isProcessing.current = true, blokując kolejne żądania
        // dopóki retryTimer nie wywołaj reset po backoffDelay
        setTimeout(() => setIsCapturing(false), 150);
      }
    }, intervalMs);
  };

  const stopCaptureLoop = () => {
    if (captureInterval.current) window.clearInterval(captureInterval.current);
    captureInterval.current = null;
  };

  // Phase 4: Manual snapshot for master agent queries
  const captureSnapshot = useCallback(async (): Promise<string | null> => {
    if (!videoRef.current || !canvasRef.current) return null;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(videoRef.current, 0, 0, 640, 480);
    const base64 = canvasRef.current.toDataURL('image/jpeg', 0.7).split(',')[1];

    try {
      // BUG-04 FIX: Snapshot też potrzebuje 90s timeout (ten sam model qwen3-vl:4b)
      const responseText = await fetchWithTimeout(
        aiService.analyzeVision(base64, 'image/jpeg', 'Opisz dokładnie co widzisz na obrazie.'),
        90000
      );

      setResults(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        text: `[SNAPSHOT] ${responseText}`,
        type: 'ANALYSIS' as const
      }, ...prev].slice(0, 50));

      return responseText;
    } catch (err: any) {
      console.error("Snapshot Error:", err);
      return null;
    }
  }, []);

  const triggerVoiceResponse = async (text: string) => {
    if (isAiSpeaking) return;

    // Phase 1.4: Don't speak if main chat is active (unless high priority - handled by caller)
    if (isMainChatActive) return;

    try {
      const ctx = await ensureAudioContext();

      // Check if context is usable
      if (ctx.state === 'closed') {
        console.warn('Video analysis audio context is closed, creating new one');
        audioContextOut.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        await audioContextOut.current.resume();
      }

      const audioData = await aiService.synthesizeSpeech(text, 'pl');
      if (audioData) {
        setIsAiSpeaking(true);
        // Use context's actual sample rate instead of hardcoded 24000
        const buffer = await decodeAudioData(new Uint8Array(audioData), ctx, ctx.sampleRate, 1);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.onended = () => setIsAiSpeaking(false);
        source.start();
        console.log("Audio playback started");
        setResults(prev => [{ timestamp: new Date().toLocaleTimeString(), text: `🔊 Mówię: "${text.substring(0, 30)}..."`, type: 'SYSTEM' as const }, ...prev].slice(0, 50));
        setResults(prev => [{ timestamp: new Date().toLocaleTimeString(), text: text, type: 'VOICE' as const }, ...prev].slice(0, 50));
      }
    } catch (err: any) {
      console.error("TTS Error:", err);
      setResults(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        text: `TTS ERROR: ${err.message || 'Audio playback failed'}`,
        type: 'SYSTEM' as const
      }, ...prev].slice(0, 50));
      setIsAiSpeaking(false);
    }
  };

  const toggleGuardian = async () => {
    if (isActive) {
      stopGuardian();
    } else {
      setLoading(true);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 } });
        if (videoRef.current) videoRef.current.srcObject = stream;
        // FIX #3: Cross-applet camera bridge — expose stream globally
        (window as any).nexusVisionStream = stream;
        (window as any).nexusSnapshot = async () => {
          const s = (window as any).nexusVisionStream as MediaStream | null;
          if (!s || s.getVideoTracks().length === 0) {
            // Fallback: try one-shot camera access
            try {
              const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
              (window as any).nexusVisionStream = tempStream;
            } catch (e) {
              return { error: 'Kamera niedostępna', code: 'NO_CAMERA' };
            }
          }
          try {
            const track = ((window as any).nexusVisionStream as MediaStream).getVideoTracks()[0];
            const capture = new (window as any).ImageCapture(track);
            const blob = await capture.takePhoto();
            return new Promise<{ base64: string }>(resolve => {
              const reader = new FileReader();
              reader.onload = () => resolve({ base64: (reader.result as string).split(',')[1] });
              reader.readAsDataURL(blob);
            });
          } catch (e) {
            // Canvas fallback for browsers without ImageCapture
            const video = document.querySelector('video');
            if (!video) return { error: 'Brak elementu video', code: 'NO_VIDEO' };
            const cv = document.createElement('canvas');
            cv.width = video.videoWidth || 640;
            cv.height = video.videoHeight || 480;
            cv.getContext('2d')!.drawImage(video, 0, 0, cv.width, cv.height);
            const dataUrl = cv.toDataURL('image/jpeg', 0.8);
            return { base64: dataUrl.split(',')[1] };
          }
        };
        setIsActive(true);
        setErrorCount(0);
        setLastError(null);
        setError(null);
        setResults(prev => [{
          timestamp: new Date().toLocaleTimeString(),
          text: "SYSTEM: Obserwator zainicjowany pomyślnie. Rozpoczęto monitorowanie.",
          type: 'SYSTEM' as const
        }, ...prev].slice(0, 50));
      } catch (err: any) { 
        console.error("Camera access failed:", err);
        setError("Błąd kamery: " + err.message); 
      }
      finally { setLoading(false); }
    }
  };

  const stopGuardian = () => {
    if (videoRef.current?.srcObject) {
      (videoRef.current.srcObject as MediaStream).getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
    // FIX #3: Clear cross-applet camera bridge on stop
    (window as any).nexusVisionStream = null;
    (window as any).nexusSnapshot = null;
    setIsActive(false);
    setErrorCount(0);
    setLastError(null);
    setRetryCountdown(null);
    // Clear all retry timers
    if (retryTimer.current) clearTimeout(retryTimer.current);
    if (countdownTimer.current) clearInterval(countdownTimer.current);
    retryTimer.current = null;
    countdownTimer.current = null;
    isProcessing.current = false;
  };

  const toggleVoiceCommand = async () => {
    if (isListening) {
      mediaRecorder.current?.stop();
      setIsListening(false);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioStream.current = stream;
        mediaRecorder.current = new MediaRecorder(stream);
        audioChunks.current = [];
        const ctx = await ensureAudioContext();
        analyser.current = ctx.createAnalyser();
        const source = ctx.createMediaStreamSource(stream);
        source.connect(analyser.current);
        monitorSilence();
        mediaRecorder.current.ondataavailable = (event) => {
          if (event.data.size > 0) audioChunks.current.push(event.data);
        };
        mediaRecorder.current.onstop = async () => {
          const audioBlob = new Blob(audioChunks.current, { type: 'audio/wav' });
          try {
            const text = await aiService.transcribeAudio(audioBlob);
            if (text) {
              setResults(prev => [{ timestamp: new Date().toLocaleTimeString(), text: `VOICE COMMAND: "${text}"`, type: 'VOICE' as const }, ...prev].slice(0, 50));
              await processVoiceCommand(text);
            }
          } catch (e) { console.error("Transcription failed", e); }
          finally {
            if (silenceTimer.current) clearInterval(silenceTimer.current);
            audioStream.current?.getTracks().forEach(t => t.stop());
            audioStream.current = null;
          }
        };
        mediaRecorder.current.start();
        setIsListening(true);
      } catch (e) { console.error("Mic access denied", e); }
    }
  };

  const monitorSilence = () => {
    if (!analyser.current) return;
    const bufferLength = analyser.current.fftSize;
    const dataArray = new Uint8Array(bufferLength);
    let silenceStart = Date.now();
    const SILENCE_THRESHOLD = 0.05;
    const SILENCE_DURATION = 1200;
    const checkVolume = () => {
      if (!analyser.current || !mediaRecorder.current || mediaRecorder.current.state !== 'recording') return;
      analyser.current.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        const normalized = (dataArray[i] - 128) / 128;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / bufferLength);
      if (rms < SILENCE_THRESHOLD) {
        if (Date.now() - silenceStart > SILENCE_DURATION) {
          mediaRecorder.current?.stop();
          setIsListening(false);
        }
      } else { silenceStart = Date.now(); }
    };
    silenceTimer.current = setInterval(checkVolume, 100);
  };

  const processVoiceCommand = async (command: string) => {
    const lowerCmd = command.toLowerCase();

    // Handle simple commands first
    if (lowerCmd.includes('status') || lowerCmd.includes('raport')) {
      await triggerVoiceResponse(`Obserwator aktywny. Poziom czułości ${sensitivity}. ${errorCount > 0 ? `Ostatni błąd: ${lastError}` : 'Brak błędów.'}`);
    } else if (lowerCmd.includes('snapshot') || lowerCmd.includes('zdjęcie')) {
      await captureSnapshot();
    } else if (lowerCmd.includes('pause') || lowerCmd.includes('pauza')) {
      setVisionMode('paused');
      await triggerVoiceResponse("Monitoring wstrzymany.");
    } else if (lowerCmd.includes('resume') || lowerCmd.includes('wznów')) {
      setVisionMode('auto');
      await triggerVoiceResponse("Monitoring wznowiony.");
    } else {
      // For other commands, send to LLM for intelligent response
      try {
        const response = await aiService.runAgentSession(`Komenda głosowa: "${command}". Odpowiedz naturalnie po polsku jako asystent obserwatora wizyjnego.`, [
          { role: 'user', parts: [{ text: 'Jesteś inteligentnym asystentem obserwatora wizyjnego. Odpowiadaj po polsku w sposób naturalny i pomocny.' }] }
        ]);

        if (response.text) {
          await triggerVoiceResponse(response.text);
        } else {
          await triggerVoiceResponse("Przepraszam, nie rozumiem tej komendy.");
        }
      } catch (error) {
        console.error("LLM processing failed:", error);
        await triggerVoiceResponse("Wystąpił błąd podczas przetwarzania komendy.");
      }
    }
  };

  if (!mounted) return null;

  return (
    <div ref={containerRef} className="flex flex-col h-full bg-[#131722] overflow-hidden min-h-[600px] relative pb-10">
      <style>{`
          .react-grid-layout { position: relative; transition: height 200ms ease; }
          .react-grid-item { transition: all 200ms ease; transition-property: left, top; }
          .react-grid-item.cssTransforms { transition-property: transform; }
          .react-grid-item.resizing { z-index: 1; will-change: width, height; }
          .react-grid-item.react-draggable-dragging { transition: none; z-index: 3; will-change: transform; }
          .react-grid-item.react-grid-placeholder { background: rgba(59, 130, 246, 0.2); border: 2px dashed rgba(59, 130, 246, 0.5); opacity: 0.5; z-index: 2; transition: none; }
          .drag-handle { cursor: grab; }
          .drag-handle:active { cursor: grabbing; }
          .panel-wrapper { height: 100%; display: flex; flex-direction: column; background: #1e222d; border: 1px solid #2a2e39; border-radius: 0.5rem; overflow: hidden; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5); }
          .panel-wrapper:hover { border-color: rgba(59, 130, 246, 0.3); }
      `}</style>

      <div className="absolute -top-10 right-4 z-50 flex gap-2">
                <button onClick={() => setIsLocked(!isLocked)} className={`text-[9px] px-2 py-1 rounded border transition-colors ${isLocked ? 'bg-red-900/40 text-red-400 border-red-800' : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'}`}>
                    <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Locked' : 'Unlocked'}
                </button>
        <button onClick={() => { setLayouts({ lg: DEFAULT_LAYOUT }); localStorage.removeItem('video_analysis_layout'); }} className="text-[9px] bg-slate-800 text-slate-400 hover:text-white px-2 py-1 rounded border border-slate-700">
          <i className="fas fa-sync-alt mr-1"></i> Reset Layout
        </button>
      </div>

      {/* NAGŁÓWEK GŁÓWNY */}
      <div className={`flex justify-between items-start mb-4 z-10 px-2 ${!isLocked ? 'drag-handle' : 'cursor-default'} w-full flex-shrink-0`}>
        <h2 className="text-xl font-black text-white flex items-center gap-3 uppercase italic tracking-tighter">
          Wizja <span className="text-purple-500">Ciągły Obserwator</span>
          {isActive && (
            <div className="flex items-center gap-2">
              {isMainChatActive ? (
                <span className="text-[9px] bg-yellow-600 text-white px-2 py-1 rounded font-black uppercase">
                  Chat Aktywny - Monitoring Cichy
                </span>
              ) : (
                <span className="text-[9px] bg-red-600 text-white px-2 py-1 rounded animate-pulse font-black uppercase">
                  Monitoring na żywo
                </span>
              )}
              <div className={`w-2 h-2 rounded-full ${isCapturing ? 'bg-white scale-150 shadow-[0_0_10px_white]' : 'bg-red-500'}`}></div>
            </div>
          )}
        </h2>
        <div className="flex gap-3">
          <select
            value={visionMode}
            onChange={(e) => setVisionMode(e.target.value as VisionMode)}
            disabled={!isActive}
            className="bg-[#2a2e39] border border-gray-600 text-slate-300 text-[10px] font-black uppercase px-2 py-1 rounded disabled:opacity-30 focus:outline-none"
          >
            <option value="auto">Auto</option>
            <option value="on-demand">Na Żądanie</option>
            <option value="paused">Pauza</option>
          </select>
          <button onClick={toggleVoiceCommand} disabled={!isActive} className={`px-4 py-2 rounded text-[10px] font-black uppercase transition-all ${isListening ? 'bg-green-600 text-white animate-pulse' : 'bg-[#2a2e39] border border-gray-600 text-slate-300 hover:bg-slate-600'} disabled:opacity-30`}>
            <i className={`fas ${isListening ? 'fa-stop' : 'fa-microphone'} mr-2`}></i> {isListening ? 'Stop' : 'Komenda'}
          </button>
          <button onClick={toggleGuardian} disabled={loading} className={`px-6 py-2 rounded text-[10px] font-black uppercase transition-all shadow-lg ${isActive ? 'bg-red-600/80 hover:bg-red-500 text-white border border-red-500/50' : 'bg-purple-600/80 hover:bg-purple-500 text-white border border-purple-500/50'}`}>
            {loading ? 'Bootowanie...' : isActive ? 'Wyłącz Obserwatora' : 'Uruchom Obserwatora'}
          </button>
        </div>
      </div>

      {retryCountdown !== null && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded p-3 mb-4 mx-2 flex items-center gap-3">
          <i className="fas fa-exclamation-triangle text-orange-500"></i>
          <div className="flex-1">
            <p className="text-[10px] font-black text-orange-400 uppercase">Błąd Połączenia z Modelem Wizji</p>
            <p className="text-[9px] text-orange-300">{lastError}</p>
          </div>
          <div className="text-right">
            <p className="text-[9px] text-orange-400 uppercase">Ponowna próba za</p>
            <p className="text-lg font-black text-orange-500">{retryCountdown}s</p>
          </div>
        </div>
      )}

      
                <div className="flex justify-between items-center sm:mb-4 mb-2 px-2 bg-[#131722]/80 border border-[#2a2e39] rounded py-2">
                    <div className="flex items-center gap-2">
                        <button onClick={() => setIsLocked(!isLocked)} className={`text-[10px] px-3 py-1.5 rounded transition-all font-bold ${isLocked ? 'bg-red-500/20 text-red-400 border border-red-500/50 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : 'bg-green-500/20 text-green-400 border border-green-500/50'}`}>
                            <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Blokada Włączona' : 'Odblokowane (Przeciągaj)'}
                        </button>
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 text-red-500 rounded border border-red-500/50 text-[10px] animate-pulse font-bold">
                        <i className="fas fa-video-slash"></i>
                        <span>{error}</span>
                      </div>
                    )}
                </div>
    <ResponsiveGridLayout
        className="layout flex-1"
        width={width}
        layouts={isLocked ? Object.fromEntries(Object.entries(layouts).map(([k, v]) => [k, v.map((i: any) => ({ ...i, static: true }))])) : layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
        rowHeight={50}
        onLayoutChange={onLayoutChange}
                // @ts-ignore
                isDraggable={!isLocked}
                // @ts-ignore
                isResizable={!isLocked}
        // @ts-ignore
        draggableHandle=".drag-handle"
        margin={[12, 12]}
      >
        {/* CAMERA PANEL */}
        <div key="camera" className="panel-wrapper">
          <div className={`p-2 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
            <div className="flex items-center gap-2">
              <i className="fas fa-camera text-blue-500"></i>
              <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Video Feed</h2>
            </div>
            <i className="fas fa-grip-lines text-slate-600"></i>
          </div>
          <div className="flex-1 relative bg-black flex items-center justify-center overflow-hidden">
            <video ref={videoRef} autoPlay playsInline muted className={`w-full h-full object-cover ${isActive ? 'brightness-100' : 'grayscale brightness-50'}`} />
            <canvas ref={canvasRef} width="640" height="480" className="hidden" />
            {isActive && visionMode === 'on-demand' && (
              <button
                onClick={captureSnapshot}
                className="absolute bottom-4 right-4 bg-purple-600 text-white px-4 py-2 rounded text-[10px] font-black uppercase shadow-lg hover:bg-purple-500 transition-all z-10"
              >
                <i className="fas fa-camera mr-2"></i> Snapshot
              </button>
            )}
            {!isActive && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-slate-500 font-bold uppercase tracking-[0.2em] text-xs opacity-50 drop-shadow-md">
                  FEED OFFLINE
                </span>
              </div>
            )}
          </div>
        </div>

        {/* SETTINGS PANEL */}
        <div key="controls" className="panel-wrapper">
          <div className={`p-2 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
            <div className="flex items-center gap-2">
              <i className="fas fa-sliders-h text-orange-500"></i>
              <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Vision Controls</h2>
            </div>
            <i className="fas fa-grip-lines text-slate-600"></i>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#0a0c10] custom-scrollbar">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="flex justify-between text-[9px] font-bold text-slate-500 uppercase">
                  <span>Przechwytywanie</span>
                  <span className="text-purple-400">{settings.visionFps}Hz</span>
                </div>
                <input type="range" min="0.1" max="5.0" step="0.1" value={settings.visionFps} onChange={(e) => setKernel(prev => ({ ...prev, visionFps: parseFloat(e.target.value) }))} className="w-full accent-purple-500" />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-[9px] font-bold text-slate-500 uppercase">
                  <span>Brama Ruchu</span>
                  <span className="text-orange-400">{sensitivity}%</span>
                </div>
                <input type="range" min="1" max="20" step="1" value={sensitivity} onChange={(e) => setSensitivity(parseInt(e.target.value))} className="w-full accent-orange-500" />
              </div>
              
              <div className="space-y-2 col-span-2">
                <div className="flex justify-between text-[9px] font-bold text-slate-500 uppercase">
                  <span>Model Wizyjny</span>
                </div>
                <select
                  value={selectedVisionModel}
                  onChange={(e) => {
                    setSelectedVisionModel(e.target.value);
                    localStorage.setItem('omnigate_observer_vision_model', e.target.value);
                  }}
                  className="w-full bg-[#131722] text-slate-300 text-[10px] px-2 py-1.5 rounded border border-[#2a2e39] outline-none"
                >
                  {availableModels.filter(m => m.tags?.includes('vision')).map(m => (
                    <option key={m.id} value={m.id}>{m.id}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2 pt-2 border-t border-[#2a2e39]">
              <p className="text-[10px] font-black text-slate-500 uppercase">Dyrektywy Logiki Rdzenia / Pre-Prompt</p>
              <textarea value={guardianPrompt} onChange={(e) => setGuardianPrompt(e.target.value)} className="w-full h-full min-h-[80px] bg-[#131722] border border-[#2a2e39] rounded p-3 text-[10px] text-slate-300 focus:border-purple-500/50 outline-none font-mono resize-none custom-scrollbar" />
            </div>

            {errorCount > 0 && (
              <div className="flex items-center gap-2 text-orange-500 text-[10px] font-bold bg-orange-500/10 p-2 rounded border border-orange-500/20">
                <i className="fas fa-exclamation-circle"></i>
                <span>{errorCount} kolejnych błędów - backoff aktywny</span>
              </div>
            )}
          </div>
        </div>

        {/* LOGS PANEL */}
        <div key="logs" className="panel-wrapper">
          <div className={`p-2 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
            <div className="flex items-center gap-2">
              <i className="fas fa-terminal text-green-500"></i>
              <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Observer Intelligence Log</h2>
            </div>
            <div className="flex items-center gap-2">
              {isMainChatActive && (
                <span className="text-[8px] bg-yellow-500/20 text-yellow-500 px-2 py-0.5 rounded font-bold uppercase">
                  Tryb Cichy
                </span>
              )}
              <i className="fas fa-grip-lines text-slate-600"></i>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#0a0c10] custom-scrollbar">
            {results.length === 0 && (
              <div className="h-full flex items-center justify-center text-slate-600 opacity-50 flex-col gap-2">
                <i className="fas fa-robot text-2xl"></i>
                <span className="text-[10px] uppercase font-bold tracking-[0.2em] text-center">Brak Logów<br />System Oczekuje</span>
              </div>
            )}
            {results.map((res, i) => (
              <div key={i} className={`animate-in slide-in-from-right-2 duration-300 border-l-2 pl-3 py-2 ${res.type === 'VOICE' ? 'border-purple-500 bg-purple-500/10' :
                res.type === 'SYSTEM' ? 'border-orange-500 bg-orange-500/10' :
                  res.type === 'NOMINAL' ? 'border-green-500 bg-green-500/5' :
                    res.type === 'FALLBACK' ? 'border-yellow-500 bg-yellow-500/5' :
                      res.type === 'HIGH_THREAT' ? 'border-red-600 bg-red-600/20 animate-pulse' :
                        'border-red-500 bg-red-500/10'
                } rounded-r`}>
                <div className="flex justify-between items-center mb-1">
                  <span className={`text-[8px] font-black uppercase tracking-widest ${res.type === 'VOICE' ? 'text-purple-400' :
                    res.type === 'SYSTEM' ? 'text-orange-400' :
                      res.type === 'NOMINAL' ? 'text-green-400' :
                        res.type === 'FALLBACK' ? 'text-yellow-400' :
                          res.type === 'HIGH_THREAT' ? 'text-red-500' :
                            'text-red-400'
                    }`}>
                    {res.type === 'NOMINAL' ? 'STATUS NORMALNY' :
                      res.type === 'VOICE' ? 'NADAWANIE' :
                        res.type === 'SYSTEM' ? 'KERNEL' :
                          res.type === 'FALLBACK' ? 'WĘZEŁ LOKALNY' :
                            res.type === 'HIGH_THREAT' ? '⚠️ ZAGROŻENIE WYSOKIE' :
                              'WYKRYTO ZAGROŻENIE'}
                  </span>
                  <span className="text-[8px] font-mono text-slate-500 font-bold">{res.timestamp}</span>
                </div>
                <p className={`text-[10px] font-mono leading-relaxed ${res.type === 'NOMINAL' ? 'text-slate-400' : res.type === 'HIGH_THREAT' ? 'text-red-300 font-bold' : 'text-slate-200'}`}>{res.text}</p>
              </div>
            ))}
          </div>
        </div>
      </ResponsiveGridLayout>
    </div>
  );
};

export default VideoAnalysis;
